import Loader from 'apollo-react/components/Loader';
import _ from 'lodash';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import AttachmentPreviewDialog from './AttachmentPreviewDialog';
import DesktopMainSection from './DesktopMainSection';
import MobileMainSection from './MobileMainSection';
import PageWrapper from './PageWrapper';
import { getAttachmentModalOpen } from './selectors';
import {
  clearEntityDisplay,
  closeAttachmentModal,
  displayEntity,
} from '../../actions/EntityDisplayActions';
import { clearUnsynchronizedEntity } from '../../actions/OfflineDataActions';
import { clearPanelLayout } from '../../actions/PanelLayoutActions';
import { addRecentEvent } from '../../actions/RecentEventsActions';
import { clearWorkflow } from '../../actions/WorkflowActions';
import Layout from '../../components/Layout';
import PopupWindowDialog from '../../components/PopupWindowDialog';
import withSubmitFormDialog from '../../components/withSubmitFormDialog';
import { PANEL_CONFIGURATIONS } from '../../constants/PanelLayoutConstants';
import { useFormSelector } from '../../custom-hooks';
import { useWindowDimensions } from '../../custom-hooks/useWindowDimensions';
import { clearFormDesigner } from '../../form-designer/actions/form-manager.actions';
import AttachmentModal from '../../form-designer/components/AttachmentModal';
import SelectedSubGrids from '../../form-designer/components/SelectedSubGrids';
import SynchronizeChangesDialog from '../../form-designer/components/SynchronizeChangesDialog';
import { FORM_KEYS } from '../../form-designer/constants/form-manager.constants';
import { FormContext } from '../../form-designer/context';
import {
  getNetworkStatus,
  getPanelLayout,
  makeGetEntityPropertyValue,
} from '../../form-designer/selectors/common.selectors';
import { getAttachmentGrid } from '../../form-designer/selectors/runtime.selectors';
import { clearValidationStorage } from '../../form-designer/utils/validationStorage.utils';
import { getIsWorkflowRequestPending } from '../../selectors/WorkflowSelectors';

function EntityDisplay({ location }) {
  const [wrapperHeight, setWrapperHeight] = useState(0);

  const dispatch = useDispatch();

  const recordNumber = useFormSelector(makeGetEntityPropertyValue, {
    formKey: FORM_KEYS.DETAIL,
    propertyName: 'UserKey',
  });
  const [prevRecordNumber, setPrevRecordNumber] = useState(recordNumber);
  const revisionNumber = useFormSelector(makeGetEntityPropertyValue, {
    formKey: FORM_KEYS.DETAIL,
    propertyName: 'RecordRevisionColumn',
  });
  const attachmentGrid = useFormSelector(getAttachmentGrid, { formKey: FORM_KEYS.STEP });
  const { config } = useSelector(getPanelLayout);
  const isOnline = useSelector(getNetworkStatus);

  const attachmentModalOpen = useSelector(getAttachmentModalOpen);
  const isWorkflowRequestPending = useSelector(getIsWorkflowRequestPending);

  const queryString = location.search;
  const qsParams = qs.parse(queryString) || {};
  const isDetailOnly = _.isNil(qsParams.stepId);

  const isMiddlePanelClosed = [
    PANEL_CONFIGURATIONS.MIDDLE_COLLAPSED,
    PANEL_CONFIGURATIONS.ONLY_RIGHT,
  ].includes(config);

  const { isMobileDevice } = useWindowDimensions();

  useEffect(() => {
    dispatch(
      displayEntity(
        qsParams.itmId,
        qsParams.entityType,
        qsParams.formKey,
        qsParams.stepId,
        qsParams.workflowId,
        qsParams.containerWorkflowKey,
        qsParams.workflowKey,
        qsParams.parentWorkflowId,
        isDetailOnly
      )
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(
    () => () => {
      batch(() => {
        dispatch(clearUnsynchronizedEntity());
        dispatch(clearFormDesigner(FORM_KEYS.STEP));
        dispatch(clearFormDesigner(FORM_KEYS.DETAIL));
        dispatch(clearPanelLayout());
        dispatch(clearEntityDisplay());
        dispatch(clearWorkflow());
        clearValidationStorage();
      });
    },
    [dispatch, location]
  );

  useEffect(() => {
    document.title = 'Step Form';
  }, []);

  useEffect(() => {
    if (!_.isNil(recordNumber) && recordNumber !== prevRecordNumber && isOnline) {
      const queryString = location.search;
      const qsParams = qs.parse(queryString) || {};

      dispatch(
        addRecentEvent({
          virtualFilePath: location.pathname,
          entityType: qsParams.entityType,
          entityKey: revisionNumber ? `${recordNumber} [${revisionNumber}]` : recordNumber,
          queryString: qs.stringify(_.pick(qsParams, ['entityType', 'formKey', 'itmId'])),
        })
      );

      setPrevRecordNumber(recordNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordNumber, isOnline]);

  return (
    <Layout>
      <PageWrapper onHeightChange={setWrapperHeight}>
        {isMobileDevice ? (
          <MobileMainSection isDetailOnly={isDetailOnly} />
        ) : (
          <DesktopMainSection wrapperHeight={wrapperHeight} />
        )}

        {isWorkflowRequestPending && <Loader isInner />}

        {/* we want to render Step form selected sub grids, even if the panel is closed */}
        {isMiddlePanelClosed && (
          <FormContext.Provider value={FORM_KEYS.STEP}>
            <SelectedSubGrids />
          </FormContext.Provider>
        )}

        <AttachmentPreviewDialog />
        <SynchronizeChangesDialog />
        <PopupWindowDialog />
        <AttachmentModal
          open={attachmentModalOpen}
          onClose={() => dispatch(closeAttachmentModal())}
          entityName={attachmentGrid?.entityName}
          propsFormKey={FORM_KEYS.STEP}
          gridId={attachmentGrid?.controlId}
        />
      </PageWrapper>
    </Layout>
  );
}

export default withSubmitFormDialog(EntityDisplay);
